import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  GeoJSON,
  useMap,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
import "leaflet-draw";
import "../../i18n/leafletDraw";
import i18n from "../../i18n";
import { Box } from "@mui/material";
import BaseMapToggle from "../../components/BaseMapToggle";
import {
  clearTargetBounds,
  setTargetBounds,
  getEffectiveBaseLayer,
} from "../../reducers/MapSlice";
import { url } from "../../api/axios_client";
import { useTileTemplateRefresh } from "../../hooks/useTileTemplateRefresh";
import { validateArea } from "../../api/FlaskAPI";
import { getErrorMessage } from "../../api/errors";

const DEFAULT_CENTER = { lat: 16.162, lng: 30.2496 };
const DEFAULT_ZOOM = 6;

const VALID_COLOR = "#1976d2";
const INVALID_COLOR = "#d32f2f";

function MapView() {
  const dispatch = useDispatch();
  const map = useMap();
  const targetBounds = useSelector((state) => state.mapState.targetBounds);

  useEffect(() => {
    if (!targetBounds) return;

    if (targetBounds) {
      // Fit the map to the target bounds
      map.fitBounds(targetBounds);
      dispatch(clearTargetBounds());
    }
  }, [dispatch, map, targetBounds]);

  return null;
}

function DrawHandler({ onAreasChange, boundaryMethod }) {
  const layersRef = useRef(new Map());

  const getCoordinates = useCallback((layer) => {
    if (layer instanceof L.Circle) {
      const center = layer.getLatLng();
      const radius = layer.getRadius();
      const points = 64;
      const coordinates = [];
      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * 2 * Math.PI;
        const dx = radius * Math.cos(angle);
        const dy = radius * Math.sin(angle);
        const latOffset = dy / 111320;
        const lngOffset =
          dx / (111320 * Math.cos((center.lat * Math.PI) / 180));
        coordinates.push([center.lng + lngOffset, center.lat + latOffset]);
      }
      return [coordinates];
    }
    const geoJson = layer.toGeoJSON();
    return geoJson.geometry.coordinates;
  }, []);

  const setLayerStyle = useCallback((layer, valid) => {
    if (!layer) return;
    const color = valid === false ? INVALID_COLOR : VALID_COLOR;
    layer.setStyle({
      color,
      fillColor: color,
      weight: 2,
      fillOpacity: 0.2,
    });
  }, []);

  const handleCreated = useCallback(
    async (e) => {
      const layer = e.layer;
      const id = layer._leaflet_id;
      const coordinates = getCoordinates(layer);
      layersRef.current.set(id, layer);

      onAreasChange((prev) => [...prev, { id, coordinates, valid: null }]);

      try {
        const response = await validateArea({
          coordinates: coordinates[0],
          crs: "EPSG:4326",
        });
        const isValid = response.status === 200 && response.data === true;
        if (!isValid) {
          toast.warning(i18n.t("area.toasts.invalidDrawn"));
        }
        setLayerStyle(layer, isValid);
        onAreasChange((prev) =>
          prev.map((a) => (a.id === id ? { ...a, valid: isValid } : a)),
        );
      } catch (error) {
        toast.error(
          getErrorMessage(error, i18n.t("area.toasts.validateFailed")),
        );
        setLayerStyle(layer, false);
        onAreasChange((prev) =>
          prev.map((a) => (a.id === id ? { ...a, valid: false } : a)),
        );
      }
    },
    [getCoordinates, onAreasChange, setLayerStyle],
  );

  const handleEdited = useCallback(
    async (e) => {
      const updates = new Map();
      e.layers.eachLayer((layer) => {
        updates.set(layer._leaflet_id, getCoordinates(layer));
      });

      const ids = Array.from(updates.keys());
      onAreasChange((prev) =>
        prev.map((a) =>
          updates.has(a.id)
            ? { ...a, coordinates: updates.get(a.id), valid: null }
            : a,
        ),
      );
      ids.forEach((id) => {
        const layer = layersRef.current.get(id);
        if (layer) setLayerStyle(layer, null);
      });

      await Promise.all(
        ids.map(async (id) => {
          const coordinates = updates.get(id);
          const layer = layersRef.current.get(id);
          try {
            const response = await validateArea({
              coordinates: coordinates[0],
              crs: "EPSG:4326",
            });
            const isValid = response.status === 200 && response.data === true;
            if (!isValid) {
              toast.warning(i18n.t("area.toasts.invalidDrawn"));
            }
            setLayerStyle(layer, isValid);
            onAreasChange((prev) =>
              prev.map((a) => (a.id === id ? { ...a, valid: isValid } : a)),
            );
          } catch (error) {
            toast.error(
              getErrorMessage(error, i18n.t("area.toasts.validateFailed")),
            );
            setLayerStyle(layer, false);
            onAreasChange((prev) =>
              prev.map((a) => (a.id === id ? { ...a, valid: false } : a)),
            );
          }
        }),
      );
    },
    [getCoordinates, onAreasChange, setLayerStyle],
  );

  const handleDeleted = useCallback(
    (e) => {
      const deletedIds = new Set();
      e.layers.eachLayer((layer) => {
        deletedIds.add(layer._leaflet_id);
        layersRef.current.delete(layer._leaflet_id);
      });
      onAreasChange((prev) => prev.filter((a) => !deletedIds.has(a.id)));
    },
    [onAreasChange],
  );

  if (boundaryMethod !== "draw") return null;

  return (
    <FeatureGroup key={boundaryMethod}>
      <EditControl
        position="topleft"
        onCreated={handleCreated}
        onEdited={handleEdited}
        onDeleted={handleDeleted}
        draw={{
          rectangle: true,
          circle: true,
          polyline: false,
          marker: false,
          polygon: true,
          circlemarker: false,
        }}
        edit={{
          edit: true,
          remove: true,
        }}
      />
    </FeatureGroup>
  );
}

export default function MapComponent({
  token,
  areas,
  onAreasChange,
  boundaryMethod,
}) {
  const dispatch = useDispatch();
  const tileLayer = useSelector((state) => state.mapState.tileLayer);
  const addProjectSatelliteLayer = useSelector(
    (state) => state.mapState.addProjectSatelliteLayer,
  );
  const addProjectPlanetLayer = useSelector(
    (state) => state.mapState.addProjectPlanetLayer,
  );
  const defaultmapLayer = useSelector(
    (state) => state.mapState.defaultmapLayer,
  );
  const satelliteTilesAvailable = useSelector(
    (state) => state.mapState.satelliteTilesAvailable,
  );
  const tilesLimits = useSelector((state) => state.mapState.tilesLimits);
  const tileBan = useSelector((state) => state.mapState.tileBan);

  const {
    handleSatelliteTileError,
    handlePlanetTileError,
    handleDefaultMapTileError,
  } = useTileTemplateRefresh({ addProjectToken: token });

  const [drawnAreas, setDrawnAreas] = useState([]);

  useEffect(() => {
    if (boundaryMethod !== "draw") setDrawnAreas([]);
  }, [boundaryMethod]);

  useEffect(() => {
    if (!areas?.geojson || areas.source === "draw") return;
    const bounds = L.geoJSON(areas.geojson).getBounds();
    if (bounds.isValid()) {
      dispatch(setTargetBounds(bounds));
    }
  }, [areas, dispatch]);

  useEffect(() => {
    const allValid = drawnAreas.every((a) => a.valid === true);
    const valid = drawnAreas.length === 0 ? null : allValid ? true : false;

    onAreasChange({
      id: `drawn-${Date.now()}`,
      source: "draw",
      valid,
      geojson: {
        type: "FeatureCollection",
        features: drawnAreas.map((a) => ({
          type: "Feature",
          geometry: {
            type: "MultiPolygon",
            coordinates: [a.coordinates],
          },
          properties: {},
        })),
      },
    });
  }, [drawnAreas, onAreasChange]);

  const isDrawing = boundaryMethod === "draw";

  const { disableSatellite, effectiveLayer, toggleLayer } = useMemo(
    () =>
      getEffectiveBaseLayer({
        tileLayer,
        tileBanned: tileBan.banned,
        isDrawing,
        drawingMode: "project",
        planetAvailable: satelliteTilesAvailable.planetTiles,
        planetTileUrl: addProjectPlanetLayer.tileUrl,
        planetFailed: addProjectPlanetLayer.failed,
        satelliteAvailable: satelliteTilesAvailable.satelliteTiles,
        satelliteTileUrl: addProjectSatelliteLayer.tileUrl,
        defaultmapAvailable: satelliteTilesAvailable.defaultmapTiles,
        defaultmapTileUrl: defaultmapLayer.tileUrl,
      }),
    [
      tileLayer,
      tileBan.banned,
      isDrawing,
      satelliteTilesAvailable.planetTiles,
      addProjectPlanetLayer.tileUrl,
      addProjectPlanetLayer.failed,
      satelliteTilesAvailable.satelliteTiles,
      addProjectSatelliteLayer.tileUrl,
      satelliteTilesAvailable.defaultmapTiles,
      defaultmapLayer.tileUrl,
    ],
  );

  const maxZoom = tilesLimits.max_zoom ?? 18;

  return (
    <>
      <Box
        className="glass-panel"
        sx={{
          position: "fixed",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          px: 1,
          py: 0.75,
        }}
      >
        <BaseMapToggle
          value={toggleLayer}
          disableSatellite={disableSatellite}
        />
      </Box>

      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        zoomControl={false}
        style={{ width: "100%", height: "100vh" }}
      >
        <MapView />
        <DrawHandler
          onAreasChange={setDrawnAreas}
          boundaryMethod={boundaryMethod}
        />

        {effectiveLayer === "planet" && addProjectPlanetLayer.tileUrl && (
          <TileLayer
            key={addProjectPlanetLayer.tileId}
            attribution={addProjectPlanetLayer.attribution}
            url={`${url}${addProjectPlanetLayer.tileUrl}`}
            maxNativeZoom={maxZoom}
            maxZoom={maxZoom}
            updateWhenIdle={true}
            keepBuffer={3}
            eventHandlers={{
              tileerror: handlePlanetTileError,
            }}
          />
        )}

        {effectiveLayer === "satellite" && addProjectSatelliteLayer.tileUrl && (
          <TileLayer
            key={addProjectSatelliteLayer.tileId}
            attribution={addProjectSatelliteLayer.attribution}
            url={`${url}${addProjectSatelliteLayer.tileUrl}`}
            maxNativeZoom={maxZoom}
            maxZoom={maxZoom}
            updateWhenIdle={true}
            keepBuffer={3}
            eventHandlers={{
              tileerror: handleSatelliteTileError,
            }}
          />
        )}

        {effectiveLayer === "defaultmap" && defaultmapLayer.tileUrl && (
          <TileLayer
            key={defaultmapLayer.tileId}
            attribution={defaultmapLayer.attribution}
            url={`${url}${defaultmapLayer.tileUrl}`}
            maxNativeZoom={maxZoom}
            maxZoom={maxZoom}
            updateWhenIdle={true}
            keepBuffer={3}
            eventHandlers={{
              tileerror: handleDefaultMapTileError,
            }}
          />
        )}

        {areas && areas.source !== "draw" && (
          <GeoJSON
            key={areas.id}
            data={areas.geojson}
            style={() => ({
              color: areas.valid === false ? INVALID_COLOR : VALID_COLOR,
              weight: 2,
              fillColor: areas.valid === false ? INVALID_COLOR : VALID_COLOR,
              fillOpacity: 0.2,
            })}
            interactive={false}
          />
        )}
      </MapContainer>
    </>
  );
}
